import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from "../utils/axios";
import {
  UserBalanceDto,
  RewardTransactionDto,
  ConvertToUsdbDto,
  UseServicePointsDto,
  CreditVolunteerHoursDto,
  RewardsState,
  ApiResponse
} from '../types/rewards';

const initialState: RewardsState = {
  balance: null,
  transactions: [],
  loading: false,
  error: null,
  conversionLoading: false,
  conversionError: null,
  servicePaymentLoading: false,
  servicePaymentError: null,
  volunteerCreditLoading: false,
  volunteerCreditError: null,
};

// Thunks
export const getCurrentUserBalance = createAsyncThunk(
  "rewards/getCurrentUserBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<UserBalanceDto>>(
        "api/services/app/UserReward/GetCurrentUserBalance"
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error || "Failed to fetch balance",
          code: "BALANCE_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const getTransactionHistory = createAsyncThunk(
  "rewards/getTransactionHistory",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<ApiResponse<RewardTransactionDto[]>>(
        "api/services/app/UserReward/GetTransactionHistory"
      );

      if (response.data.success) {
        return response.data.result;
      } else {
        return rejectWithValue({
          message: response.data.error || "Failed to fetch transaction history",
          code: "TRANSACTION_HISTORY_FETCH_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const convertToUsdb = createAsyncThunk(
  "rewards/convertToUsdb",
  async (convertData: ConvertToUsdbDto, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<void>>(
        "api/services/app/UserReward/ConvertToUsdb",
        convertData
      );

      if (response.data.success) {
        return convertData.amount;
      } else {
        return rejectWithValue({
          message: response.data.error || "Conversion failed",
          code: "CONVERSION_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const useServicePoints = createAsyncThunk(
  "rewards/useServicePoints",
  async (serviceData: UseServicePointsDto, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<void>>(
        "api/services/app/UserReward/UseServicePoints",
        serviceData
      );

      if (response.data.success) {
        return serviceData.amount;
      } else {
        return rejectWithValue({
          message: response.data.error || "Service payment failed",
          code: "SERVICE_PAYMENT_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const creditVolunteerHours = createAsyncThunk(
  "rewards/creditVolunteerHours",
  async (volunteerData: CreditVolunteerHoursDto, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<void>>(
        "api/services/app/UserReward/CreditVolunteerHours",
        volunteerData
      );

      if (response.data.success) {
        return volunteerData;
      } else {
        return rejectWithValue({
          message: response.data.error || "Failed to credit volunteer hours",
          code: "VOLUNTEER_CREDIT_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

export const validateSocialImpactNumber = createAsyncThunk(
  "rewards/validateSocialImpactNumber",
  async (socialImpactNumber: string, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse<boolean>>(
        `api/services/app/UserReward/ValidateSocialImpactNumber?socialImpactNumber=${encodeURIComponent(socialImpactNumber)}`
      );

      if (response.data.success) {
        return {
          socialImpactNumber,
          isValid: response.data.result
        };
      } else {
        return rejectWithValue({
          message: response.data.error || "Validation failed",
          code: "VALIDATION_FAILED"
        });
      }
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "An unknown error occurred",
        code: "UNKNOWN_ERROR"
      });
    }
  }
);

// Slice
const rewardsSlice = createSlice({
  name: 'rewards',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.conversionError = null;
      state.servicePaymentError = null;
      state.volunteerCreditError = null;
    },
    clearBalance: (state) => {
      state.balance = null;
    },
    clearTransactions: (state) => {
      state.transactions = [];
    },
    updateBalanceAfterConversion: (state, action: PayloadAction<number>) => {
      if (state.balance) {
        state.balance.transferBalance -= action.payload;
      }
    },
    updateBalanceAfterServicePayment: (state, action: PayloadAction<number>) => {
      if (state.balance) {
        // This is a simplified update - actual logic should come from backend
        // For now, we'll deduct from donation balance first
        const amount = action.payload;
        if (state.balance.donationBalance >= amount) {
          state.balance.donationBalance -= amount;
        } else {
          const remaining = amount - state.balance.donationBalance;
          state.balance.donationBalance = 0;
          state.balance.volunteerBalance -= remaining;
        }
      }
    },
    updateBalanceAfterVolunteerCredit: (state, action: PayloadAction<number>) => {
      if (state.balance) {
        state.balance.volunteerBalance += action.payload * 10; // Assuming 10 points per hour
      }
    }
  },
  extraReducers: (builder) => {
    // Get Current User Balance
    builder
      .addCase(getCurrentUserBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUserBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload;
      })
      .addCase(getCurrentUserBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Transaction History
    builder
      .addCase(getTransactionHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransactionHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload;
      })
      .addCase(getTransactionHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Convert to USDB
    builder
      .addCase(convertToUsdb.pending, (state) => {
        state.conversionLoading = true;
        state.conversionError = null;
      })
      .addCase(convertToUsdb.fulfilled, (state, action) => {
        state.conversionLoading = false;
        // Update local balance immediately for better UX
        if (state.balance) {
          state.balance.transferBalance -= action.payload;
        }
      })
      .addCase(convertToUsdb.rejected, (state, action) => {
        state.conversionLoading = false;
        state.conversionError = action.payload as string;
      });

    // Use Service Points
    builder
      .addCase(useServicePoints.pending, (state) => {
        state.servicePaymentLoading = true;
        state.servicePaymentError = null;
      })
      .addCase(useServicePoints.fulfilled, (state, action) => {
        state.servicePaymentLoading = false;
        // Update local balance immediately for better UX
        if (state.balance) {
          const amount = action.payload;
          if (state.balance.donationBalance >= amount) {
            state.balance.donationBalance -= amount;
          } else {
            const remaining = amount - state.balance.donationBalance;
            state.balance.donationBalance = 0;
            state.balance.volunteerBalance -= remaining;
          }
        }
      })
      .addCase(useServicePoints.rejected, (state, action) => {
        state.servicePaymentLoading = false;
        state.servicePaymentError = action.payload as string;
      });

    // Credit Volunteer Hours
    builder
      .addCase(creditVolunteerHours.pending, (state) => {
        state.volunteerCreditLoading = true;
        state.volunteerCreditError = null;
      })
      .addCase(creditVolunteerHours.fulfilled, (state, action) => {
        state.volunteerCreditLoading = false;
        // Update local balance immediately for better UX
        if (state.balance) {
          const points = action.payload.hours * 10; // Assuming 10 points per hour
          state.balance.volunteerBalance += points;
          if (action.payload.socialImpactNumber) {
            state.balance.socialImpactNumber = action.payload.socialImpactNumber;
          }
        }
      })
      .addCase(creditVolunteerHours.rejected, (state, action) => {
        state.volunteerCreditLoading = false;
        state.volunteerCreditError = action.payload as string;
      });
  },
});

export const {
  clearErrors,
  clearBalance,
  clearTransactions,
  updateBalanceAfterConversion,
  updateBalanceAfterServicePayment,
  updateBalanceAfterVolunteerCredit
} = rewardsSlice.actions;

export default rewardsSlice.reducer;